import { useToggle } from '@bygghemmait/hooks';
import { Button, LoaderButton } from 'components/Button';
import { DisplayAddress } from 'components/CustomerSupportForms/Components/Address/DisplayAddress';
import {
  BlueSectionContainer,
  GreySectionContainer,
  SubmitContainer
} from 'components/CustomerSupportForms/Components/SectionContainer/SectionContainer';
import { OrderFormProps } from 'components/CustomerSupportForms/Utils';
import { Option, Select } from 'components/Select';
import { TextArea } from 'components/Shared/Form';
import { ChangeEvent, useEffect, useReducer } from 'react';
import { rm } from 'utils';
import ExtendedInfo from 'views/CustomerPage/OrderHistory/ExtendedInfo';
import { DisplayMode } from 'views/CustomerPage/OrderHistory/OrderLine';
import {
  getReasonOptions,
  getSubjectOptions,
  isValid,
  returnAndExchangeFormId,
  returnAndExchangeInitialState,
  reducer,
  subjectFieldId,
  getReasonFieldId
} from './ReturnAndExchangeHelper';
import styles from './styles.scss';
import { createZendeskRequest, orderFieldId, productFieldId } from '../utils/util';
import { postCreateRequest } from 'services/ApiCustomerSupportFormService';
import { IBasicResponse } from 'models/IBasicResponse';
import { productChanged, reasonChanged, subjectChanged } from '../utils';

const CHANGE_MAX_LENGTH = 1000;

export const ReturnAndExchangeForm = ({
  order,
  error,
  returnUrl,
  onSuccess,
  formTitle
}: OrderFormProps) => {
  const [loading, toggleLoading] = useToggle(false);
  const [internalState, internalDispatch] = useReducer(reducer, returnAndExchangeInitialState);

  useEffect(() => {
    if (order) {
      internalDispatch({ type: 'address', value: order?.deliveryAddress });
      internalDispatch({ type: 'email', value: order?.email });
    }
    if (error) {
      internalDispatch({
        type: 'hasError',
        value: true
      });
    }
  }, [order, error]);

  const handleChangeInput = (_event: ChangeEvent<HTMLTextAreaElement>, newValue: string) => {
    internalDispatch({
      type: 'question',
      value: newValue
    });
    internalDispatch({
      type: 'questionError',
      value: ''
    });
  };

  const completeInternal = () => {
    toggleLoading();
    internalDispatch({ type: 'generalError', value: '' });
    if (isValid(internalState, internalDispatch)) {
      const reasonFieldId = getReasonFieldId(internalState.subject);
      const request = createZendeskRequest({
        formId: returnAndExchangeFormId,
        email: internalState.email || '',
        name: internalState.address?.name || '',
        formTitle: formTitle,
        body: internalState.question,
        formSubject: {
          fieldId: subjectFieldId,
          id: internalState.subject,
          array: getSubjectOptions()
        },
        reason: {
          fieldId: reasonFieldId,
          id: internalState.reason,
          array: getReasonOptions(internalState.subject)
        },
        orderId: order?.orderNumber,
        orderFieldId: orderFieldId,
        products: internalState.products,
        productFieldId: productFieldId
      });
      return postCreateRequest(request)
        .then(() => {
          toggleLoading();
          onSuccess();
        })
        .catch((response: IBasicResponse) => {
          toggleLoading();
          internalDispatch({ type: 'generalError', value: response.message[0] });
        });
    }

    toggleLoading();
    return Promise.resolve();
  };

  return (
    <>
      <h3 className={styles.subHeader}>{rm('customerpage_returns_products_header')}</h3>
      <GreySectionContainer>
        {order && (
          <ExtendedInfo
            order={order}
            displayMode={DisplayMode.Claims}
            returnProductChanged={p => productChanged(p, internalDispatch)}
            className={styles.products}
          />
        )}
      </GreySectionContainer>
      {internalState.productsError && (
        <GreySectionContainer error={true}>{internalState.productsError}</GreySectionContainer>
      )}
      <h3 className={styles.removeMarginBottom}>{rm('customerpage_returns_address_header')}</h3>
      <span className={styles.addressExtraText}>{rm('address_fetch_from_order')}</span>
      <GreySectionContainer>
        <DisplayAddress
          {...internalState.address}
          email={internalState.email}
          classnames={styles.address}
        />
      </GreySectionContainer>
      <h3>{rm('customerservice_zendesk_question_with_parameter', '3.')}</h3>
      <GreySectionContainer classname={styles.subjectContainer}>
        <div>
          <span>
            {rm('customerservice_question_casetype')}
            <span className={styles.requiredStar}> *</span>
          </span>
          <Select
            scrollable
            className={styles.subject}
            classNames={{ input: styles.input, icon: styles.icon, optionList: styles.optionList }}
            selectedValue={internalState.subject}
            onChange={val => subjectChanged(val as number, internalDispatch, true)}
            placeHolder="--"
          >
            {getSubjectOptions().map(option => (
              <Option key={option.id} value={option.id} label={rm(option.tag)} />
            ))}
          </Select>
          {internalState.subjectError && (
            <p className={styles.invalidMessage}>{internalState.subjectError}</p>
          )}
        </div>
        {internalState.showReason && internalState.subject && (
          <div>
            <span>
              {rm('customerservice_zendesk_reason')} <span className={styles.requiredStar}>*</span>
            </span>
            <Select
              scrollable
              className={styles.subject}
              classNames={{
                input: styles.input,
                icon: styles.icon,
                optionList: styles.optionList
              }}
              selectedValue={internalState.reason}
              onChange={val => reasonChanged(val as number, internalDispatch)}
              placeHolder="--"
            >
              {getReasonOptions(internalState.subject).map(option => (
                <Option key={option.id} value={option.id} label={rm(option.tag)} />
              ))}
            </Select>
            {internalState.reasonError && (
              <p className={styles.invalidMessage}>{internalState.reasonError}</p>
            )}
          </div>
        )}
        <div>
          <span>
            {rm('customerservice_zendesk_question')} <span className={styles.requiredStar}>*</span>
          </span>
          <TextArea
            onChange={handleChangeInput}
            textlimit={CHANGE_MAX_LENGTH}
            value={internalState.question}
          />
          <p
            className={styles.returnMsgLength}
          >{`(${internalState.question.length}/${CHANGE_MAX_LENGTH})`}</p>
          {internalState.questionError && (
            <p className={styles.invalidMessage}>{internalState.questionError}</p>
          )}
        </div>
      </GreySectionContainer>
      <SubmitContainer>
        <LoaderButton appearance="whitePillPrimary" onClick={completeInternal} loading={loading}>
          {loading ? '' : rm('customerpage_returns_submit')}
        </LoaderButton>
        <Button appearance="whitePill" href={returnUrl}>
          {rm('customerpage_returns_cancel')}
        </Button>
      </SubmitContainer>
      {internalState.generalError && (
        <BlueSectionContainer error>{internalState.generalError}</BlueSectionContainer>
      )}
    </>
  );
};
